import { ReactNode, useCallback } from 'react';
import { FlatList, useWindowDimensions } from 'react-native';

import { SizedBox } from '@/components/SizedBox';
import { LoadingSkeleton } from '@/components/SkeletonLoader';
import { Box, BoxProps, Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { MARKET_COLUMN_WIDTH } from '@/components/sbk-traditional-matchup-card/parts/MarketColumn';
import { useLeague } from '@/feature/league-hub-sbk/hooks/use-league';
import { useLeagueSport } from '@/feature/lobby-sbk/hooks/use-league-sport';
import { designSystem } from '@/styles/styles';
import { showScrollIndicator } from '@/utils/constants-platform-specific';

const MARKET_WIDTH = 210;

const TeamNameSkeleton = ({ nameWidth }: { nameWidth?: number }) => {
    const { width } = useWindowDimensions();
    const teamNameWidth = nameWidth ?? width - MARKET_WIDTH - 32 - 12; // 32 is the padding horizontal, 12 is the gap between the market and the team name
    return (
        <Column justifyContent="center">
            <Box backgroundColor={ds.colorToken.background.neutral.subtler} borderRadius={'r6'}>
                <LoadingSkeleton
                    skeletonColor={designSystem.colors.neutral300}
                    height={24}
                    borderRadius={'r6'}
                    width={teamNameWidth}
                />
            </Box>
        </Column>
    );
};

type TeamSkeletonProps = {
    borderTopLeftRadius: BoxProps['borderTopLeftRadius'];
    borderTopRightRadius: BoxProps['borderTopRightRadius'];
    borderBottomRightRadius: BoxProps['borderBottomRightRadius'];
    borderBottomLeftRadius: BoxProps['borderBottomLeftRadius'];
};

const TeamBoxSkeleton = ({
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius,
    width: boxWidth = MARKET_WIDTH,
}: TeamSkeletonProps & { width?: number }) => (
    <Column>
        <Box
            backgroundColor={ds.colorToken.background.neutral.subtler}
            borderTopLeftRadius={borderTopLeftRadius}
            borderTopRightRadius={borderTopRightRadius}
            borderBottomRightRadius={borderBottomRightRadius}
            borderBottomLeftRadius={borderBottomLeftRadius}
            height={62}
        >
            <LoadingSkeleton
                skeletonColor={designSystem.colors.neutral300}
                width={boxWidth}
                height={62}
                borderTopLeftRadius={borderTopLeftRadius}
                borderTopRightRadius={borderTopRightRadius}
                borderBottomRightRadius={borderBottomRightRadius}
                borderBottomLeftRadius={borderBottomLeftRadius}
            />
        </Box>
    </Column>
);

const TeamRowSkeleton = ({
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius,
}: TeamSkeletonProps) => (
    <Row gap="s16">
        <TeamNameSkeleton />
        <TeamBoxSkeleton
            borderTopLeftRadius={borderTopLeftRadius}
            borderTopRightRadius={borderTopRightRadius}
            borderBottomRightRadius={borderBottomRightRadius}
            borderBottomLeftRadius={borderBottomLeftRadius}
        />
    </Row>
);

type EventItemSkeletonProps = {
    showHeader?: boolean;
    showFooter?: boolean;
};

const EventItemSkeletonLayout = ({
    showHeader = true,
    showFooter = true,
    headerWidth,
    children,
}: EventItemSkeletonProps & { headerWidth: number; children: ReactNode }) => (
    <Box width="100%" paddingHorizontal="s16" gap="s12">
        {showHeader ? (
            <Box alignItems="flex-end">
                <Box backgroundColor={ds.colorToken.background.neutral.subtler} borderRadius={'r6'}>
                    <LoadingSkeleton
                        skeletonColor={designSystem.colors.neutral300}
                        width={headerWidth}
                        height={20}
                        borderRadius={'r6'}
                    />
                </Box>
            </Box>
        ) : null}
        {children}
        {showFooter ? (
            <Box backgroundColor={ds.colorToken.background.neutral.subtler} borderRadius={'r6'}>
                <LoadingSkeleton skeletonColor={designSystem.colors.neutral300} height={24} borderRadius={'r6'} />
            </Box>
        ) : null}
    </Box>
);

const EventItemSkeletonDefault = (props: EventItemSkeletonProps) => (
    <EventItemSkeletonLayout {...props} headerWidth={MARKET_WIDTH}>
        <Box gap="s3">
            <TeamRowSkeleton
                borderTopLeftRadius="r16"
                borderTopRightRadius="r16"
                borderBottomRightRadius="r4"
                borderBottomLeftRadius="r4"
            />
            <TeamRowSkeleton
                borderTopLeftRadius="r4"
                borderTopRightRadius="r4"
                borderBottomRightRadius="r16"
                borderBottomLeftRadius="r16"
            />
        </Box>
    </EventItemSkeletonLayout>
);

const SoccerRowSkeleton = ({
    nameWidth,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
}: TeamSkeletonProps & { nameWidth?: number }) => (
    <Row alignItems="center" justifyContent="space-between">
        <TeamNameSkeleton nameWidth={nameWidth} />
        <TeamBoxSkeleton
            borderTopLeftRadius={borderTopLeftRadius}
            borderTopRightRadius={borderTopRightRadius}
            borderBottomLeftRadius={borderBottomLeftRadius}
            borderBottomRightRadius={borderBottomRightRadius}
            width={MARKET_COLUMN_WIDTH}
        />
    </Row>
);

const EventItemSkeletonSoccer = (props: EventItemSkeletonProps) => (
    <EventItemSkeletonLayout {...props} headerWidth={MARKET_COLUMN_WIDTH}>
        <Box gap="s3">
            <SoccerRowSkeleton
                borderTopLeftRadius="r16"
                borderTopRightRadius="r16"
                borderBottomLeftRadius="r4"
                borderBottomRightRadius="r4"
            />
            <SoccerRowSkeleton
                borderTopLeftRadius="r4"
                borderTopRightRadius="r4"
                borderBottomLeftRadius="r4"
                borderBottomRightRadius="r4"
            />
            <SoccerRowSkeleton
                nameWidth={40}
                borderTopLeftRadius="r4"
                borderTopRightRadius="r4"
                borderBottomLeftRadius="r16"
                borderBottomRightRadius="r16"
            />
        </Box>
    </EventItemSkeletonLayout>
);

export const EventItemSkeleton = ({ sportName, ...props }: EventItemSkeletonProps & { sportName?: string }) => {
    const sportNameLower = sportName?.toLowerCase();
    if (sportNameLower === 'soccer') {
        return <EventItemSkeletonSoccer {...props} />;
    }
    return <EventItemSkeletonDefault {...props} />;
};

const EventItemSeparator = () => <SizedBox value={44} />;

type EventListSkeletonProps = {
    leagueId: string;
    count?: number;
    hideHeader?: boolean;
};

export const EventListSkeleton = ({ leagueId, count, hideHeader = false }: EventListSkeletonProps) => {
    const { data: league } = useLeague(leagueId);
    const eventCount = Math.min(count ?? league?.event_count ?? 5, 5); // Max 5 items
    const sportName = useLeagueSport(leagueId)?.name ?? '';

    const renderItem = useCallback(
        ({ index }: { index: number }) => {
            return <EventItemSkeleton sportName={sportName} showHeader={index === 0 && !hideHeader} />;
        },
        [hideHeader, sportName]
    );

    return (
        <FlatList
            data={Array.from({ length: eventCount })}
            renderItem={renderItem}
            showsVerticalScrollIndicator={showScrollIndicator}
            ItemSeparatorComponent={EventItemSeparator}
        />
    );
};
